// Debug script para probar la conexión con Firebase
import { historiaClinicaService } from './services/firebaseServices';
import { collection, getDocs, query, getFirestore } from 'firebase/firestore';
import app from './config/firebase';

// Inicializar Firestore directamente aquí
const db = getFirestore(app);

export const debugFirebase = {
  // Función para obtener TODAS las historias sin filtros
  async getAllHistorias() {
    console.log('=== DEBUG: Obteniendo TODAS las historias ===');
    try {
      const q = query(
        collection(db, 'historiasClinicas')
        // Sin orderBy para evitar problema de índice
      );
      const querySnapshot = await getDocs(q);
      
      console.log('📊 Total de documentos en historiasClinicas:', querySnapshot.size);
      
      const todasLasHistorias = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const historia = {
          id: doc.id,
          ...data,
          fecha: data.fecha || data.fechaCreacion?.toDate?.()?.toISOString?.()?.split('T')[0]
        };
        todasLasHistorias.push(historia);
        console.log(`📄 ID: ${doc.id}`);
        console.log(`   pacienteId: "${data.pacienteId}"`);
        console.log(`   motivoConsulta: "${data.motivoConsulta}"`);
        console.log(`   fecha: ${historia.fecha}`);
        console.log(`   activo: ${data.activo}`);
        console.log('   ---');
      });
      
      // Ordenar manualmente por fecha
      todasLasHistorias.sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));
      
      return { success: true, data: todasLasHistorias };
    } catch (error) {
      console.error('❌ Error obteniendo todas las historias:', error);
      return { success: false, error: error.message };
    }
  },

  // Función para probar la creación de una historia clínica de prueba
  async testCreateHistoria(pacienteId) {
    console.log('=== DEBUG: Creando historia de prueba ===');
    const testData = {
      pacienteId: pacienteId,
      pacienteNombre: 'Paciente Test',
      motivoConsulta: 'Prueba de debug - ' + new Date().toLocaleTimeString(),
      anamnesis: 'Esta es una historia de prueba para verificar Firebase',
      veterinario: 'Dr. Debug',
      fecha: new Date().toISOString().split('T')[0],
      observaciones: 'Historia creada para debug'
    };
    
    try {
      console.log('📝 Datos a guardar:', testData);
      const result = await historiaClinicaService.createHistoriaClinica(testData);
      console.log('✅ Resultado de crear historia:', result);
      
      if (result.success) {
        console.log('🎉 Historia creada exitosamente');
        // Inmediatamente verificar si se puede leer
        console.log('🔍 Verificando lectura inmediata...');
        await this.testGetHistorias(pacienteId);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error creando historia de prueba:', error);
      return { success: false, error: error.message };
    }
  },

  // Función para probar la lectura de historias
  async testGetHistorias(pacienteId) {
    console.log('=== DEBUG: Obteniendo historias ===');
    console.log('🔑 Paciente ID:', pacienteId);
    console.log('📝 Tipo del ID:', typeof pacienteId);
    
    try {
      const result = await historiaClinicaService.getHistoriasByPaciente(pacienteId);
      console.log('📋 Resultado de obtener historias:', result);
      
      if (result.success) {
        console.log(`✅ Encontradas ${result.data.length} historias para este paciente`);
        result.data.forEach((historia, index) => {
          console.log(`📄 Historia ${index + 1}:`);
          console.log(`   ID: ${historia.id}`);
          console.log(`   Motivo: ${historia.motivoConsulta}`);
          console.log(`   Fecha: ${historia.fecha}`);
          console.log(`   ---`);
        });
      } else {
        console.log('❌ No se pudieron obtener historias:', result.error);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error obteniendo historias:', error);
      return { success: false, error: error.message };
    }
  },

  // Función para verificar la estructura de datos en Firebase
  async testConnection() {
    console.log('=== DEBUG: Verificando conexión con Firebase ===');
    
    // Primero ver todas las historias
    console.log('🔍 Paso 1: Verificando todas las historias en la base...');
    await this.getAllHistorias();
    
    // Intentar crear una historia con ID temporal
    const testId = 'test-debug-' + Date.now();
    console.log('🔍 Paso 2: Creando historia de prueba...');
    const createResult = await this.testCreateHistoria(testId);
    
    if (createResult.success) {
      console.log('✅ Creación exitosa');
      
      // Intentar leer las historias
      console.log('🔍 Paso 3: Leyendo historias...');
      const readResult = await this.testGetHistorias(testId);
      
      if (readResult.success && readResult.data.length > 0) {
        console.log('✅ Lectura exitosa');
        console.log('🎉 Firebase está funcionando correctamente');
      } else {
        console.log('❌ Problema en la lectura');
        console.log('📋 Resultado de lectura:', readResult);
      }
    } else {
      console.log('❌ Problema en la creación');
      console.log('📋 Resultado de creación:', createResult);
    }
  },

  // Función para analizar problemas específicos
  async analyzePatientHistorias(pacienteId) {
    console.log('=== DEBUG: Análisis detallado para paciente ===');
    console.log('🔑 Paciente ID analizar:', pacienteId);
    
    // 1. Ver todas las historias
    console.log('📊 1. Todas las historias en la base:');
    const allHistorias = await this.getAllHistorias();
    
    // 2. Filtrar manualmente las que coinciden
    if (allHistorias.success) {
      const matching = allHistorias.data.filter(h => h.pacienteId === pacienteId);
      console.log(`🎯 2. Historias que coinciden con "${pacienteId}":`, matching.length);
      
      if (matching.length === 0) {
        console.log('⚠️ No hay historias con ese pacienteId exacto');
        console.log('🔍 Historias similares:');
        allHistorias.data.forEach(h => {
          if (h.pacienteId && h.pacienteId.toString().includes(pacienteId.toString())) {
            console.log(`   - pacienteId: "${h.pacienteId}" (contiene "${pacienteId}")`);
          }
        });
      } else {
        matching.forEach((h, i) => {
          console.log(`   Historia ${i+1}: ${h.motivoConsulta} (${h.fecha})`);
        });
      }
    }
    
    // 3. Usar el servicio oficial
    console.log('🔧 3. Usando servicio oficial:');
    await this.testGetHistorias(pacienteId);
  }
};

// Exponer funciones de debug globalmente para usar en consola
if (typeof window !== 'undefined') {
  window.debugFirebase = debugFirebase;
  console.log('🔧 Funciones de debug disponibles:');
  console.log('- debugFirebase.testConnection()');
  console.log('- debugFirebase.getAllHistorias()');
  console.log('- debugFirebase.testCreateHistoria(pacienteId)');
  console.log('- debugFirebase.testGetHistorias(pacienteId)');
  console.log('- debugFirebase.analyzePatientHistorias(pacienteId)');
}
